import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, ApiError } from '@/composables/useApi';

export type Role = 'admin' | 'referee';

export interface SessionUser {
    id: string;
    username: string;
    email: string;
    role: Role;
    referee_id?: string | null;
}

/**
 * Session store — owns the authenticated user record. Cookie
 * (`frvb_session`) is set by the server on /api/login; we cache the
 * user object here for instant reads.
 *
 * The system has exactly two roles: admin (full federation control)
 * and referee (own dashboard + scoresheet submission).
 */
export const useSessionStore = defineStore('session', () => {
    const user = ref<SessionUser | null>(null);
    const ready = ref(false);   // true once restore() has resolved

    const isAuthed = computed(() => user.value !== null);
    const role     = computed<Role | null>(() => user.value?.role ?? null);
    const isAdmin   = computed(() => role.value === 'admin');
    const isReferee = computed(() => role.value === 'referee');

    async function restore(): Promise<boolean> {
        try {
            user.value = await api<SessionUser>('/me');
            return true;
        } catch (e) {
            if (e instanceof ApiError && e.status === 401) {
                user.value = null;
            }
            return false;
        } finally {
            ready.value = true;
        }
    }

    async function login(username: string, password: string): Promise<void> {
        user.value = await api<SessionUser>('/login', {
            method: 'POST',
            body: { username, password }
        });
    }

    async function logout(): Promise<void> {
        try { await api('/logout', { method: 'POST' }); }
        catch { /* even on failure clear local state */ }
        user.value = null;
    }

    function canAccess(path: string): boolean {
        if (isAdmin.value) return true;
        const r = role.value;
        if (path === '/admin' || path.startsWith('/admin')) return false;
        if (path === '/new')   return false;
        if (path === '/referee'    || path.startsWith('/referee'))    return r === 'referee';
        if (path === '/scoresheet' || path.startsWith('/scoresheet')) return r === 'referee';
        return false;
    }

    function defaultRoute(): string {
        if (isAdmin.value)   return '/admin';
        if (isReferee.value) return '/referee';
        return '/login';
    }

    return {
        user, ready, isAuthed, role, isAdmin, isReferee,
        restore, login, logout,
        canAccess, defaultRoute
    };
});
